/**
 * API Client - Centralized HTTP client with authentication and error handling
 *
 * Features:
 * - Supports GET, POST, PUT, PATCH, DELETE methods
 * - Automatic JWT token handling
 * - Token refresh on expiration
 * - Session expiry handling with redirect
 * - File upload support
 * - Request/Response interceptors
 * - Timeout handling
 * - Abort controller support
 */

import { getSession, signOut } from "next-auth/react";
import { API_HOST } from "~/constants";
import {
  ApiError,
  ApiErrorCode,
  ApiRequestConfig,
  ApiResponse,
  ContentType,
  FileUploadConfig,
  HttpMethod,
} from "~/types-and-enums/apiTypes";

/**
 * Default request timeout (30 seconds)
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * Build query string from params object
 */
function buildQueryString(params: Record<string, string | number | boolean>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });
  return searchParams.toString();
}

/**
 * Create an ApiError from a Response
 */
async function createApiError(response: Response): Promise<ApiError> {
  let errorData: unknown;
  let message = response.statusText || "An error occurred";

  try {
    errorData = await response.json();
    if (typeof errorData === "object" && errorData !== null) {
      const data = errorData as Record<string, unknown>;
      message = (data.message as string) || (data.detail as string) || message;
    }
  } catch {
    // If JSON parsing fails, use text
    try {
      const text = await response.text();
      if (text) message = text;
    } catch {
      // Ignore text parsing errors
    }
  }

  // Map HTTP status to error codes
  let code: ApiErrorCode;
  switch (response.status) {
    case 401:
      code = ApiErrorCode.UNAUTHORIZED;
      break;
    case 403:
      code = ApiErrorCode.FORBIDDEN;
      break;
    case 404:
      code = ApiErrorCode.NOT_FOUND;
      break;
    case 400:
    case 422:
      code = ApiErrorCode.VALIDATION_ERROR;
      break;
    case 500:
    case 502:
    case 503:
    case 504:
      code = ApiErrorCode.SERVER_ERROR;
      break;
    default:
      code = ApiErrorCode.SERVER_ERROR;
  }

  let errors: Record<string, string[]> | undefined;

  if (errorData && typeof errorData === "object" && !Array.isArray(errorData)) {
    const maybeErrors = (errorData as Record<string, unknown>).errors;
    if (maybeErrors && typeof maybeErrors === "object" && !Array.isArray(maybeErrors)) {
      errors = maybeErrors as Record<string, string[]>;
    }
  }

  return {
    message,
    status: response.status,
    statusText: response.statusText,
    errors,
    code,
  };
}

/**
 * Check if JWT token is expired
 */
function isTokenExpired(expiresAt: number): boolean {
  const now = Math.floor(Date.now() / 1000);
  // Add 60 second buffer
  return now >= expiresAt - 60;
}

/**
 * Handle session expiration - sign out and redirect to magic login
 */
async function handleSessionExpired(): Promise<never> {
  await signOut({ redirect: false });

  // Import redirect utility
  const { redirectToMagicLoginClient } = await import("~/utils/sessionUtils");

  // Get current path to redirect back after login
  const currentPath = window.location.pathname;
  const callbackUrl = currentPath.includes("/auth/") ? undefined : currentPath;

  // Redirect to magic login
  redirectToMagicLoginClient(callbackUrl);

  throw new Error("Session expired");
}

/**
 * Get authentication headers
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const session = await getSession();

  if (!session?.user?.access_token) {
    throw new Error("No authentication token available");
  }

  // Check if token is expired
  if (session.user.expires && isTokenExpired(session.user.expires)) {
    await handleSessionExpired();
  }

  return {
    Authorization: `Bearer ${session.user.access_token}`,
  };
}

/**
 * Core fetch wrapper with timeout and abort support
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout: number = DEFAULT_TIMEOUT,
  externalSignal?: AbortSignal
): Promise<Response> {
  const controller = new AbortController();
  const { signal } = controller;

  // Handle external abort signal
  if (externalSignal) {
    externalSignal.addEventListener("abort", () => controller.abort());
  }

  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if ((error as Error).name === "AbortError") {
      throw {
        message: "Request timeout",
        status: 408,
        statusText: "Request Timeout",
        code: ApiErrorCode.TIMEOUT_ERROR,
      } as ApiError;
    }
    throw {
      message: "Network error",
      status: 0,
      statusText: "Network Error",
      code: ApiErrorCode.NETWORK_ERROR,
    } as ApiError;
  }
}

/**
 * Main API request function
 */
export async function apiRequest<T = unknown>(
  endpoint: string,
  config: ApiRequestConfig = {}
): Promise<ApiResponse<T>> {
  const {
    method = HttpMethod.GET,
    headers = {},
    body,
    params,
    requiresAuth = true,
    contentType = ContentType.JSON,
    signal,
    timeout = DEFAULT_TIMEOUT,
  } = config;

  // Build full URL
  // let url = endpoint.startsWith("http") ? endpoint : `${API_HOST}${endpoint}`; // OLD way
  let url = endpoint.startsWith("/") ? `${API_HOST}${endpoint}` : endpoint;

  // Add query parameters
  if (params && Object.keys(params).length > 0) {
    const queryString = buildQueryString(params);
    url += `${url.includes("?") ? "&" : "?"}${queryString}`;
  }

  // Build headers
  const requestHeaders: Record<string, string> = {
    ...headers,
  };

  // Add authentication header if required
  if (requiresAuth) {
    const authHeaders = await getAuthHeaders();
    Object.assign(requestHeaders, authHeaders);
  }

  // Add content type header if not multipart/form-data (handled by browser)
  if (contentType !== ContentType.FORM_DATA) {
    requestHeaders["Content-Type"] = contentType;
  }

  // Build request body
  let requestBody: BodyInit | undefined;
  if (body) {
    if (contentType === ContentType.JSON) {
      requestBody = JSON.stringify(body);
    } else if (contentType === ContentType.URL_ENCODED) {
      const formData = new URLSearchParams();
      Object.entries(body as Record<string, string>).forEach(([key, value]) => {
        formData.append(key, value);
      });
      requestBody = formData;
    } else if (contentType === ContentType.FORM_DATA && body instanceof FormData) {
      requestBody = body;
    }
  }

  // Make request
  try {
    const response = await fetchWithTimeout(
      url,
      {
        method,
        headers: requestHeaders,
        body: requestBody,
      },
      timeout,
      signal
    );

    // Handle 401 Unauthorized - token expired
    if (response.status === 401 && requiresAuth) {
      await handleSessionExpired();
    }

    // Handle error responses
    if (!response.ok) {
      const apiError = await createApiError(response);
      throw apiError;
    }

    // Parse response
    let data: T;
    const contentType = response.headers.get("content-type");

    if (contentType?.includes("application/json")) {
      data = await response.json();
    } else if (contentType?.includes("text/")) {
      data = (await response.text()) as T;
    } else {
      // For other content types, return blob
      data = (await response.blob()) as T;
    }

    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
  } catch (error) {
    // Re-throw ApiError
    if ((error as ApiError).code) {
      throw error;
    }

    // Convert unknown errors to ApiError
    throw {
      message: (error as Error).message || "An unexpected error occurred",
      status: 0,
      statusText: "Unknown Error",
      code: ApiErrorCode.NETWORK_ERROR,
    } as ApiError;
  }
}

/**
 * GET request
 */
export async function get<T = unknown>(endpoint: string, config: Omit<ApiRequestConfig, "method" | "body"> = {}) {
  return apiRequest<T>(endpoint, { ...config, method: HttpMethod.GET });
}

/**
 * POST request
 */
export async function post<T = unknown>(
  endpoint: string,
  body?: unknown,
  config: Omit<ApiRequestConfig, "method"> = {}
) {
  return apiRequest<T>(endpoint, { ...config, method: HttpMethod.POST, body });
}

/**
 * PUT request
 */
export async function put<T = unknown>(
  endpoint: string,
  body?: unknown,
  config: Omit<ApiRequestConfig, "method"> = {}
) {
  return apiRequest<T>(endpoint, { ...config, method: HttpMethod.PUT, body });
}

/**
 * PATCH request
 */
export async function patch<T = unknown>(
  endpoint: string,
  body?: unknown,
  config: Omit<ApiRequestConfig, "method"> = {}
) {
  return apiRequest<T>(endpoint, { ...config, method: HttpMethod.PATCH, body });
}

/**
 * DELETE request
 */
export async function del<T = unknown>(endpoint: string, config: Omit<ApiRequestConfig, "method" | "body"> = {}) {
  return apiRequest<T>(endpoint, { ...config, method: HttpMethod.DELETE });
}

/**
 * Upload file(s) with optional additional data
 */
export async function uploadFile<T = unknown>(
  endpoint: string,
  uploadConfig: FileUploadConfig,
  config: Omit<ApiRequestConfig, "method" | "body" | "contentType"> = {}
): Promise<ApiResponse<T>> {
  const { file, fieldName = "file", additionalData } = uploadConfig;

  const formData = new FormData();

  // Add file(s)
  if (Array.isArray(file)) {
    file.forEach((f) => formData.append(fieldName, f));
  } else {
    formData.append(fieldName, file);
  }

  // Add additional data
  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, typeof value === "object" ? JSON.stringify(value) : String(value));
      }
    });
  }

  return apiRequest<T>(endpoint, {
    ...config,
    method: HttpMethod.POST,
    body: formData,
    contentType: ContentType.FORM_DATA,
  });
}

/**
 * Download file
 */
export async function downloadFile(
  endpoint: string,
  filename: string,
  config: Omit<ApiRequestConfig, "method" | "body"> = {}
): Promise<void> {
  const response = await apiRequest<Blob>(endpoint, {
    ...config,
    method: HttpMethod.GET,
  });

  // Create blob URL and trigger download
  const blob = response.data;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
